import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import { getAutoSelectFulfillmentOfferId } from '@/lib/urgentCare/ownerOptions'
import { isUpcomingThroughScheduledCalendarDay } from '@/lib/visits/careUpcoming'

/** Shared mutation cache key so onQueryStarted auto-select and the options page share state. */
export function urgentCareSelectOfferCacheKey(orderId: string): string {
    return `urgent-care-select-offer:${orderId}`
}

/** Response from POST /api/external/urgent_care/orders */
export type CreateUrgentCareOrderResponse = {
    order_id: string
}

/**
 * Create body. Core requires `sku_id` and leaves `fulfillment_offer_id` unset
 * until a later PATCH select-offer call.
 */
export type CreateUrgentCareOrderRequest = {
    sku_id: string
}

/** Response from PATCH /api/external/urgent_care/orders/:id. */
export type SelectUrgentCareOrderOfferResponse = {
    order_id: string
}

export type SelectUrgentCareOrderOfferRequest = {
    orderId: string
    fulfillment_offer_id: string
}

/** Subset of VisitOffers offer rows used by the UC options auto-select step. */
export type UrgentCareOrderOffer = {
    fulfillment_offer_id: string
    fulfillment_owner?: {
        id?: string
        display_name?: string
        name?: string
        direct_scheduling_integrated?: boolean
        /** Square partner mark, matching visit offers `fulfillment_owner.logo_url`. */
        logo_url?: string | null
    }
    type?: string | null
    price?: { amount?: number | null; unit?: string | null } | null
    booking_url?: string | null
    availability?: string[]
}

export type UrgentCareOrderOffersResponse = {
    offers: UrgentCareOrderOffer[]
}

/** Live Solv appointment fields from order GET (`OrderReadViewService` + FO get_appointment). */
export type UrgentCareOrderAppointment = {
    id?: string | null
    status?: string | null
    start?: string | null
    encounter_class?: string | null
    is_walk_in?: boolean | null
}

/** Nested SKU from `UrgentCare::OrderBlueprint`. */
export type UrgentCareOrderSku = {
    id?: string | number | null
    name?: string | null
}

/**
 * Nested fulfillment offer on order GET (`UrgentCare::FulfillmentOfferBlueprint`).
 * Owner is used for partner branding; scheduling fields for walk-in / reschedule gates.
 */
export type UrgentCareOrderFulfillmentOffer = {
    id?: string | null
    scheduling_mode?: string | null
    fulfillment_modality?: string | null
    fulfillment_owner?: UrgentCareOrderOffer['fulfillment_owner'] | null
}

/**
 * Patient-scoped urgent care order detail
 * Aligns with `UrgentCare::OrderBlueprint` from sibling checkout work, plus
 * optional feed fields My Care may already know.
 */
export type UrgentCareOrder = {
    id: string
    state: string
    owner_appointment_id?: string | null
    booked_at?: string | null
    cancelled_at?: string | null
    fulfillment_offer_id?: string | null
    owner_location_id?: string | null
    appointment_time?: string | null
    /** Legacy feed field; prefer `sku.name` from the order blueprint. */
    sku_name?: string | null
    sku?: UrgentCareOrderSku | null
    fulfillment_offer?: UrgentCareOrderFulfillmentOffer | null
    appointment?: UrgentCareOrderAppointment | null
}

/**
 * POST body for checkout. Core permits `location_id` +
 * `appointment_time` (+ optional `waitlist`). The FE booking query uses the
 * same Core field names for the location + slot handoff.
 */
export interface UrgentCareCheckoutRequest {
    orderId: string
    location_id: string
    appointment_time?: string
    on_demand?: boolean
}

/** Booked order payload returned by a successful checkout (shape may be thin). */
export interface UrgentCareCheckoutResponse {
    id: string
    state?: string
    owner_appointment_id?: string | null
    booked_at?: string | null
}

export interface UrgentCareCancelRequest {
    orderId: string
    cancellation_reason?: string
    cancellation_reason_other?: string
}

export interface UrgentCareCancelResponse {
    id: string
    state: string
    cancelled_at?: string | null
    owner_appointment_id?: string | null
    cancellation_reason?: string | null
    cancellation_reason_other?: string | null
}

export interface UrgentCareRescheduleRequest {
    id: string
    new_time: string
}

export interface UrgentCareRescheduleResponse {
    order: {
        id: string
        state: string
        booked_at?: string | null
        owner_appointment_id?: string | null
    }
}

/** Prefer live Solv start, then local booked_at, then checkout aoe time. */
export function urgentCareOrderDisplayTime(order: UrgentCareOrder): string | null {
    return order.appointment?.start || order.booked_at || order.appointment_time || null
}

export function urgentCareOrderSkuName(order: UrgentCareOrder): string | null {
    return order.sku?.name || order.sku_name || null
}

function parseUrgentCareOrderTime(raw?: string | null): number | null {
    if (!raw) return null
    const time = new Date(raw).getTime()
    return Number.isNaN(time) ? null : time
}

function urgentCareOrderIsWalkIn(order: UrgentCareOrder): boolean {
    return Boolean(order.appointment?.is_walk_in) || order.fulfillment_offer?.scheduling_mode === 'on_demand'
}

/**
 * Self-serve cancel/reschedule stay available while the visit is still Upcoming:
 * timed slots until the appointment instant, walk-ins through that calendar day.
 * Orders with no known time stay actionable (same as My Care upcoming).
 */
export function isUrgentCareOrderStillSelfServeActionable(
    order: UrgentCareOrder,
    now: number = Date.now(),
): boolean {
    const displayTime = urgentCareOrderDisplayTime(order)
    const scheduled = parseUrgentCareOrderTime(displayTime)
    if (scheduled === null) return true

    if (urgentCareOrderIsWalkIn(order)) {
        return isUpcomingThroughScheduledCalendarDay(displayTime, now)
    }

    return scheduled >= now
}

/** Scheduled booked visits with a known clinic can pick a new slot at that location. */
export function canRescheduleUrgentCareOrder(order: UrgentCareOrder, now: number = Date.now()): boolean {
    if ((order.state ?? '').toLowerCase() !== 'booked') return false
    if (urgentCareOrderIsWalkIn(order)) return false
    if (!order.owner_location_id?.trim()) return false
    return isUrgentCareOrderStillSelfServeActionable(order, now)
}

/** Orders that AASM may still cancel (init / pending / booked) while the visit is upcoming. */
export function canCancelUrgentCareOrder(order: UrgentCareOrder, now: number = Date.now()): boolean {
    const state = (order.state ?? '').toLowerCase()
    if (state !== 'init' && state !== 'pending' && state !== 'booked') return false
    if (state !== 'booked') return true
    return isUrgentCareOrderStillSelfServeActionable(order, now)
}

/**
 * RTK Query surface for UrgentCare::Order create, detail, offer selection, offers,
 * checkout, cancel, and reschedule. Locations live in `urgentCareLocationsApi`.
 */
export const urgentCareApi = baseApi.enhanceEndpoints({ addTagTypes: ['UrgentCareOrder'] }).injectEndpoints({
    endpoints: (builder) => ({
        createUrgentCareOrder: builder.mutation<CreateUrgentCareOrderResponse, CreateUrgentCareOrderRequest>({
            query: (body) => ({
                url: apiEndpoints.urgentCare.create,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) => (result?.order_id ? [{ type: 'UrgentCareOrder', id: result.order_id }] : []),
        }),

        selectUrgentCareOrderOffer: builder.mutation<
            SelectUrgentCareOrderOfferResponse,
            SelectUrgentCareOrderOfferRequest
        >({
            query: ({ orderId, fulfillment_offer_id }) => ({
                url: apiEndpoints.urgentCare.selectOffer(orderId),
                method: 'PATCH',
                body: { fulfillment_offer_id },
            }),
            // Selection does not change the offers list; invalidating UrgentCareOrder
            // tags would refetch getUrgentCareOrderOffers and re-fire auto-select.
            // Refresh order detail only so nested fulfillment_offer / owner stay current.
            invalidatesTags: [],
            async onQueryStarted({ orderId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    void dispatch(
                        urgentCareApi.endpoints.getUrgentCareOrder.initiate(
                            { orderId },
                            { subscribe: false, forceRefetch: true },
                        ),
                    )
                } catch {
                    // Selection failures surface via the mutation hook state.
                }
            },
        }),

        getUrgentCareOrderOffers: builder.query<UrgentCareOrderOffersResponse, { orderId: string }>({
            query: ({ orderId }) => ({
                url: apiEndpoints.urgentCare.offers(orderId),
            }),
            providesTags: (_result, _error, { orderId }) => [{ type: 'UrgentCareOrder', id: orderId }],
            async onQueryStarted({ orderId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const fulfillmentOfferId = getAutoSelectFulfillmentOfferId(data.offers ?? [])
                    if (!fulfillmentOfferId) return

                    // Skip when the order already has a selected offer (e.g. booking /
                    // booked screens refetching offers after checkout invalidation).
                    try {
                        const order = await dispatch(
                            urgentCareApi.endpoints.getUrgentCareOrder.initiate({ orderId }, { subscribe: false }),
                        ).unwrap()
                        if (order.fulfillment_offer_id?.trim()) return
                    } catch {
                        // Order load failed; still attempt auto-select below.
                    }

                    await dispatch(
                        urgentCareApi.endpoints.selectUrgentCareOrderOffer.initiate(
                            { orderId, fulfillment_offer_id: fulfillmentOfferId },
                            { fixedCacheKey: urgentCareSelectOfferCacheKey(orderId) },
                        ),
                    ).unwrap()
                } catch {
                    // Offer load or auto-select failures surface via query/mutation hook state.
                }
            },
        }),

        getUrgentCareOrder: builder.query<UrgentCareOrder, { orderId: string }>({
            query: ({ orderId }) => apiEndpoints.urgentCare.detail(orderId),
            providesTags: (_result, _error, { orderId }) => [{ type: 'UrgentCareOrder', id: orderId }],
        }),

        checkoutUrgentCareOrder: builder.mutation<UrgentCareCheckoutResponse | void, UrgentCareCheckoutRequest>({
            query: ({ orderId, ...body }) => ({
                url: apiEndpoints.urgentCare.checkout(orderId),
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, { orderId }) => [
                { type: 'UrgentCareOrder', id: orderId },
                { type: 'UrgentCareOrderLocations', id: orderId },
                'MyCare',
            ],
        }),

        cancelUrgentCareOrder: builder.mutation<UrgentCareCancelResponse, UrgentCareCancelRequest>({
            query: ({ orderId, ...body }) => ({
                url: apiEndpoints.urgentCare.detail(orderId),
                method: 'DELETE',
                body,
            }),
            invalidatesTags: (_result, _error, { orderId }) => [
                { type: 'UrgentCareOrder', id: orderId },
                { type: 'UrgentCareOrderLocations', id: orderId },
                'MyCare',
            ],
        }),

        rescheduleUrgentCareOrder: builder.mutation<UrgentCareRescheduleResponse, UrgentCareRescheduleRequest>({
            query: ({ id, new_time }) => ({
                url: apiEndpoints.urgentCare.reschedule(id),
                method: 'PATCH',
                body: { new_time },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'UrgentCareOrder', id },
                { type: 'UrgentCareOrderLocations', id },
                'MyCare',
            ],
        }),
    }),
    overrideExisting: false,
})

export const {
    useCreateUrgentCareOrderMutation,
    useSelectUrgentCareOrderOfferMutation,
    useGetUrgentCareOrderOffersQuery,
    useGetUrgentCareOrderQuery,
    useCheckoutUrgentCareOrderMutation,
    useCancelUrgentCareOrderMutation,
    useRescheduleUrgentCareOrderMutation,
} = urgentCareApi
